import Heading from "@afa-shared/afa-components/dist/Heading";
import Text from "@afa-shared/afa-components/dist/Text";
import ImageBlock from "@components/ImageBlock";
import {
  ImageBlockType_imageBlock,
  ImageBlockType_imageBlock_link,
} from "@components/ImageBlock/queries/contentfulTypes/ImageBlockType";
import { StandardLink } from "@components/Link/StandardLink";
import TableBlock from "@components/TableBlock";
import { TableBlockType_tableBlock } from "@components/TableBlock/queries/contentfulTypes/TableBlockType";
import { TextList } from "@components/TextList";
import { TextListType_textList } from "@components/TextList/queries/contentfulTypes/TextListType";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import { LinkType, getInternalOrExternalIcon } from "@utils/linkIconHelper";
import { getFileSize, isNullOrEmpty } from "@utils/string";
import { replaceImageVaultBasePath } from "@utils/urlResolver";
import clsx from "clsx";
import React from "react";

import { Context } from "types/context/Context";
import type { GAClickLink } from "types/ga";

import richTextRendererStyles from "./richTextRenderer.module.css";

interface IRichTextRendererProps {
  json: any;
  links?: any;
  pageTextArea?: boolean;
  context?: Context;
  isFooter?: boolean;
}
interface LinkProps {
  url: string;
  name: string;
}

/**
 * Recursively extracts the plain text content from rendered rich text children.
 * Using join(" ") on the children array stringifies React elements (e.g. text
 * with marks such as bold, or <br/> from renderText) to "[object Object]", which
 * is why mailto and other formatted links could render as "[object Object]".
 */
const extractTextFromChildren = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") {
        return String(child);
      }
      if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
        return extractTextFromChildren(child.props.children);
      }
      return "";
    })
    .join("");

const resolveSectionTitle = (context: Context): string | undefined => {
  return (
    context?.accordionHeading ?? context?.sectionBlock?.sectionBlockHeading ?? context?.pageHeading
  );
};

const buildTrackingData = (context: Context, isFooter: boolean): GAClickLink => ({
  sectionTitle: resolveSectionTitle(context),
  sectionBlockIndex: context?.sectionBlockIndex ?? context?.sectionBlock?.sectionBlockIndex,
  linkType: isFooter ? "footer" : context?.contactUsBlock ? "contact" : "click",
  insurance: context?.insuranceName,
});

function richTextOptions(links: any, richTextContext: Context, isFooter: boolean) {
  const entryMap = new Map();
  const linkMap = new Map();

  if (links?.entries) {
    if (links?.entries?.block) {
      for (const entry of links?.entries?.block) {
        entryMap.set(entry?.sys?.id, entry);
      }
    }
    if (links?.entries?.hyperlink) {
      for (const link of links?.entries?.hyperlink) {
        linkMap.set(link?.sys?.id, link);
      }
    }
  }

  const trackingData = buildTrackingData(richTextContext, isFooter);

  return {
    renderMark: {
      [MARKS.CODE]: (text) => <Text children={text} variant={"paragraph"} />,
    },
    renderText: (text) => {
      const sanitizedText = text.replace(/\u2028/g, " "); //Replace LINE SEPERATOR (shift enter, soft break) character with normal whitespace
      return sanitizedText.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <Text variant="paragraph" children={children} />,
      [BLOCKS.HEADING_2]: (node, children) => (
        <Heading
          className={`${richTextRendererStyles.extendedHeading} ${richTextRendererStyles.h2}`}
          variant="h2"
          maxWidth={782}
          children={children}
        />
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <Heading
          className={`${richTextRendererStyles.extendedHeading} ${richTextRendererStyles.h3}`}
          variant="h3"
          maxWidth={782}
          children={children}
        />
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <Heading
          className={`${richTextRendererStyles.extendedHeading} ${richTextRendererStyles.h4}`}
          variant="h4"
          maxWidth={782}
          children={children}
        />
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <Heading
          className={`${richTextRendererStyles.extendedHeading} ${richTextRendererStyles.h4}`}
          variant="h5"
          maxWidth={782}
          children={children}
        />
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <Heading
          className={`${richTextRendererStyles.extendedHeading} ${richTextRendererStyles.h4}`}
          variant="h6"
          maxWidth={782}
          children={children}
        />
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className={richTextRendererStyles.extendedUl} children={children} />
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className={richTextRendererStyles.extendedOl} children={children} />
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className={richTextRendererStyles.extendedLi} children={children} />
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <Text className={richTextRendererStyles.extendedBlockQuote} variant="quote">
          {children}
        </Text>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const entry = entryMap.get(node?.data?.target?.sys?.id);
        if (entry?.__typename === "TableBlock") {
          const tableData: TableBlockType_tableBlock = {
            __typename: "TableBlock",
            name: "TableBlock i brödtext",
            table: entry?.table,
            heading: entry?.heading,
            audienceCollection: null,
            rightAlignedColumns: entry?.rightAlignedColumns || null,
            sys: entry?.sys,
            source: entry?.source,
          };

          return (
            <TableBlock
              className={richTextRendererStyles.extendedTableBlock}
              blockData={tableData}
            />
          );
        }
        if (entry?.__typename === "ImageBlock") {
          const linkData: ImageBlockType_imageBlock_link = {
            __typename: "Link",
            globalLinkItem: entry?.link?.globalLinkItem,
            reference: entry?.link?.reference,
            url: entry?.link?.url,
            newTab: entry?.link?.newTab,
          };

          const imageData: ImageBlockType_imageBlock = {
            __typename: "ImageBlock",
            name: "ImageBlock i brödtext",
            ivImage: entry?.ivImage,
            imageDescription: entry?.imageDescription,
            link: linkData,
            enableZoom: entry?.enableZoom,
            audienceCollection: undefined,
            shareImage: false,
            squareImage: null,
            shareHeading: "",
            shareLinkText: "",
            shareRichtext: "",
            sys: entry?.sys?.id,
          };
          return (
            <ImageBlock
              className={richTextRendererStyles.extendedImageBlock}
              blockData={imageData}
            />
          );
        }
        if (entry?.__typename === "TextList") {
          const textList: TextListType_textList = {
            __typename: "TextList",
            listStyle: entry?.listStyle,
            texts: entry?.texts,
            sys: entry?.sys,
          };

          return (
            <TextList className={richTextRendererStyles.extendedTextList} blockData={textList} />
          );
        }
        return null;
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const linkType = getInternalOrExternalIcon(node?.data?.uri, null, null);
        const text = extractTextFromChildren(children);
        const href = node?.data?.uri;
        const rel = href.includes("?openSector") ? "nofollow" : null;

        return (
          <StandardLink
            className={clsx(richTextRendererStyles.extendedLink, {
              [richTextRendererStyles.extendedInternalLink]:
                linkType === LinkType.Internal && richTextContext?.contactUsBlock?.allowLinkIcon,
            })}
            type={linkType}
            text={text}
            href={node?.data?.uri}
            variant={"link"}
            rel={rel}
            hideIcon={
              richTextContext?.contactUsBlock?.allowLinkIcon
                ? false
                : linkType !== LinkType.External
            }
            trackingData={trackingData}
          />
        );
      },

      // eslint-disable-next-line sonarjs/cognitive-complexity
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const link = linkMap?.get(node?.data?.target.sys.id);
        const text = extractTextFromChildren(children);

        if (link?.__typename === "FileBlock") {
          const file = link?.imageVaultFile && link?.imageVaultFile[0];
          const fileExtension = file?.fileExtension?.toUpperCase().slice(0, 3);
          const fileSize = getFileSize(file?.fileSize);

          const fileData =
            fileExtension && fileSize
              ? ` (${fileExtension}, ${fileSize})`
              : fileExtension
                ? ` (${fileExtension})`
                : fileSize
                  ? ` (${fileSize})`
                  : "";
          const linkText = children[0]?.props?.children
            ? children[0]?.props?.children
            : children[0]?.filter((x) => x) + fileData;

          return (
            file && (
              <StandardLink
                className={`${richTextRendererStyles.extendedLink}`}
                href={replaceImageVaultBasePath(file?.mediaUrl)}
                text={linkText}
                hideIcon={true}
                trackingData={trackingData}
              />
            )
          );
        } else {
          const linkData: LinkProps = {
            url: link?.url,
            name: text || link?.url,
          };

          return linkData?.url ? (
            <StandardLink
              className={clsx(richTextRendererStyles.extendedLink, {
                [richTextRendererStyles.extendedInternalLink]:
                  richTextContext?.contactUsBlock?.allowLinkIcon,
              })}
              text={linkData?.name}
              href={linkData?.url}
              hideIcon={richTextContext?.contactUsBlock?.allowLinkIcon ? false : true}
              trackingData={trackingData}
            />
          ) : (
            <span>{linkData?.name}</span>
          );
        }
      },
    },
  };
}

const RichTextRenderer = ({
  json,
  links,
  pageTextArea,
  context,
  isFooter = false,
}: IRichTextRendererProps): React.ReactNode => {
  const options = richTextOptions(links, context, isFooter);

  let trimmedJson = json;

  /* Removes empty lines from bottom of rich text area. If empty, nothing is rendered */
  const trimLastLine = (jsonToTrim) => {
    const trimmedContent = jsonToTrim.content.slice(0, -1);
    jsonToTrim = { ...jsonToTrim, content: trimmedContent };
    const length = jsonToTrim.content.length;
    const lastElement = jsonToTrim?.content[length - 1]?.content[0]?.value;
    const contentLength = jsonToTrim?.content[length - 1]?.content?.length;
    const nodeType = jsonToTrim?.content[length - 1]?.content[0]?.nodeType;
    if (isNullOrEmpty(lastElement?.trim()) && contentLength === 1 && nodeType === "text") {
      return trimLastLine(jsonToTrim);
    }
    return jsonToTrim;
  };

  const length = json?.content?.length;
  const lastElement = json?.content[length - 1]?.content[0]?.value;
  const contentLength = json?.content[length - 1]?.content?.length;
  if (isNullOrEmpty(lastElement?.trim()) && contentLength === 1) {
    trimmedJson = trimLastLine(trimmedJson);
  }

  return trimmedJson?.content?.length > 0 ? (
    <div
      className={richTextRendererStyles.richTextRenderer}
      data-blockname={pageTextArea && "brödtext"}
    >
      {documentToReactComponents(trimmedJson, options)}
    </div>
  ) : (
    <></>
  );
};

export default RichTextRenderer;
